<script setup lang="ts">
import {
  ref,
  watch,
  computed,
  onUpdated,
  onUnmounted,
} from 'vue';

import {
  LpTable,
  LpSkeletonText,
} from '@thehotelsnetwork/lampo';

import useSectionState from '@/state/useSectionState';
import useFilterState from '@/state/useFilterState';
import useMainState from '@/state/useMainState';

// Components
import BmDiffBubble from '@/components/DiffBubble/DiffBubble.vue';

import chunkArray from '@/utils/chunkArray';

import type { Table, Property } from '@/declarations/main';

type TableProps = Pick<Table, 'tableRows' | 'columnDescription' | 'id' | 'loading'>

type TableCellData = {
  value?: string | number;
  pastValue?: string | number;
  formattedDiff?: string;
  diff?: number;
  loading?: boolean;
  ready?: boolean;
  propertyId?: number;
};

type TableRowData = Record<string, TableCellData>;

type TableColumnDef = {
  label: string;
  key: string;
  isDataRow?: boolean;
};

const props = defineProps<TableProps>();

const emit = defineEmits(['update']);

const pageSize = 10;
const currentPage = ref(1);
const pagination = computed(() => ({
  pageSize,
  currentPage: currentPage.value,
}));

const tableData = ref<TableRowData[]>([]);
const columns = ref<TableColumnDef[]>([]);

const {
  compsetNames,
  compsets,
  isBreakdownOverviewSection,
  sectionName,
  isDefaultOverviewSection,
  setBreakdownPropertyCallList,
  loadingProperties,
  isLoadingProperty,
  isFailedProperty,
  isCompsetValid,
} = useSectionState();

const { userProperties } = useMainState();

const { ctpTag } = useFilterState();

const isDisparitiesSection = !!sectionName.value.match(/disparities/i);

const getBreakdownCompsets = () => compsets.value
  .filter(isCompsetValid)
  .map((c) => {
    const bdCompset = {
      ...c,
    };

    if (c.subTag) {
      bdCompset.compset = c.subTag;
    }

    return bdCompset;
  });

const handleBreakdownPropertyCallList = (page: number) => {
  if (!isBreakdownOverviewSection.value) { return; }

  if (!props.columnDescription) { return; }

  const chunk = chunkArray(tableData.value, pageSize)[page - 1] ?? [];
  const { columnDescription } = props;
  const list = chunk
    .filter((row: TableRowData) => !row[columnDescription]?.ready)
    .map((row: TableRowData) => row[columnDescription]?.propertyId);

  setBreakdownPropertyCallList(list);
};

const handlePageChange = (page: number) => {
  currentPage.value = page;
  handleBreakdownPropertyCallList(page);
};

const compsetsHeader = computed(() => {
  if (isBreakdownOverviewSection.value) {
    // modify "My properties" into "hotel"
    return getBreakdownCompsets().map((c) => c.compset);
  }

  if (isDefaultOverviewSection.value) {
    // show only "My properties"
    return compsets.value
      .filter((c) => c.compsetTag.match(/own|thn/i))
      .map((c) => c.compset);
  }

  return compsetNames.value;
});

const generateRows = () => {
  let imputedRows;

  // fill not yet loaded property data in "hotels breakdown" view
  if (isBreakdownOverviewSection.value) {
    imputedRows = (userProperties.value as Property[]).map((p) => {
      // use existing row or create a fake one with the next property
      const currentRow = props.tableRows.filter((r: any) => r.propertyId === p.propertyId)[0];

      const loading = isLoadingProperty(p.propertyId);
      const failed = isFailedProperty(p.propertyId);

      let rowData;
      if (currentRow && !loading) {
        rowData = {
          ...currentRow,
          ready: true,
          loading,
        };
      } else if (failed) {
        rowData = {
          ready: true,
          rowName: p.name,
          propertyId: p.propertyId,
          values: [],
          loading: false,
        };
      } else {
        rowData = {
          ready: false,
          rowName: p.name,
          propertyId: p.propertyId,
          values: [],
          loading: true,
        };
      }

      return rowData;
    });
  } else {
    imputedRows = props.tableRows ?? [];
  }

  // fill spaces for missing data
  const imputedData = imputedRows.reduce((acc: any[], curr: any) => {
    const item = { ...curr };
    const baseCompsets = isBreakdownOverviewSection.value
      ? getBreakdownCompsets() : compsets.value;

    if (baseCompsets.length) {
      baseCompsets
        .filter((c) => compsetsHeader.value.indexOf(c.compset) !== -1)
        .forEach((c) => {
          if (!item.values.filter((i: any) => i.compset === c.compset).length) {
            item.values.push({
              patternId: c.patternId,
              compset: c.compset,
              isOwn: c.isOwn,
              value: undefined,
              pastValue: undefined,
              diff: undefined,
            });
          }
        });
    }

    item.values.sort((prev: any, next: any) => prev.patternId - next.patternId);

    acc.push(item);

    return acc;
  }, []);

  columns.value = compsetsHeader.value.reduce((acc, compset) => {
    acc.push({
      label: compset,
      key: compset,
      isDataRow: true,
    });

    return acc;
  }, [
    {
      label: props.columnDescription,
      key: props.columnDescription,
    },
  ]);

  const localData: TableRowData[] = [];

  tableData.value = imputedData.forEach((row: any, rowIndex: number) => {
    localData.push({
      [props.columnDescription]: {
        value: row.rowName,
        ready: row.ready,
        propertyId: row.propertyId,
      },
    });

    row.values?.forEach((field: any) => {
      const currentRow = localData[rowIndex];
      if (!currentRow) { return; }
      currentRow[field.compset] = {
        value: field.value,
        pastValue: field.pastValue,
        formattedDiff: field.formattedDiff,
        diff: field.diff,
        loading: row.loading,
      };
    });
  });

  tableData.value = localData;
};

if (isBreakdownOverviewSection.value) {
  watch(loadingProperties, generateRows, { immediate: true });

  // handle properties to load on first mount
  handleBreakdownPropertyCallList(1);
} else {
  watch(() => props.loading, generateRows, { immediate: true });
}

onUnmounted(() => {
  emit('update', null);
});

onUpdated(() => {
  const finished = (!isBreakdownOverviewSection.value
      || !loadingProperties.value.length)
    && !!tableData.value.length;

  if (finished) {
    const getTableExportData = () => ({
      tableHeaders: columns.value.map(({ label }) => label),
      tableRows: tableData.value,
    });

    emit('update', getTableExportData);
  } else {
    emit('update', null);
  }
});
</script>

<template>
  <LpTable
    :data="tableData"
    :pagination="pagination"
    :columns="columns"
    :loading="(!isBreakdownOverviewSection && loading) || columns.length <= 1"
    @page-change="handlePageChange"
  >
    <template
      v-for="({ key, isDataRow }) in columns"
      :key="key"
      #[key]="{ data }"
    >
      <template v-if="data[key]">
        <LpSkeletonText v-if="data[key].loading" />

        <div
          v-else
          class="flex gap-2 items-center"
          :class="{ 'font-bold' : !isDataRow }"
        >
          <span> {{ data[key].value || '-' }} </span>

          <template v-if="ctpTag && isDataRow">
            <span class="text-base-700"> {{ data[key].pastValue || '-' }} </span>
            <BmDiffBubble
              :diff="data[key].diff"
              :formatted-diff="data[key].formattedDiff"
              :allow-colors="true"
              :inverse="isDisparitiesSection"
            />
          </template>
        </div>
      </template>
    </template>
  </LpTable>
</template>
